import ItemApi from '@apis/item/ItemApi';
import orderQueries from '@apis/queries/orderQueries';
import projectQueries from '@apis/queries/projectQueries';
import useShop from '@hooks/shop/useShop';
import useTestUser from '@hooks/test/useTestUser';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { CartStatus } from '@models/cart';
import { ItemResponse } from '@models/item';
import {
    CreateOrderRequest, OptionWithGroup, OrderLine, OrderOption,
} from '@models/order';
import { OrderResponse, OrderOptionRow } from '@models/order/orderLog';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { useFlow } from '@stackflow/react';
import { defaultOrderFields } from '@store/defaultOrderFields';
import { orderActions } from '@store/order/orderSlice';
import { useQuery } from '@tanstack/react-query';
import { toReward } from '@utils/orderUtils';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

function useReOrder(orderLog: OrderResponse) {
    const { t } = useTranslation('orderLog');
    const {
        shopId, shopName, orderItems, orderNo, orderType,
    } = orderLog;
    const projectId = useProjectId();
    const dispatch = useDispatch();
    const { data: orderAble } = useQuery(orderQueries.orderAble(orderNo));

    const generateOptions = (
        orderOptions: OrderOptionRow[],
        item: ItemResponse,
        optionType: 'PERSONAL' | 'DEFAULT',
    ): OptionWithGroup[] => {
        const { defaultOptionGroups, personalOptionGroups } = item;
        const optionGroups = optionType === 'DEFAULT'
            ? defaultOptionGroups : personalOptionGroups;
        const availableOptions = optionGroups
            .map((group) => group.options)
            .flat();

        const options: OptionWithGroup[] = [];
        for (let j = 0; j < orderOptions.length; j += 1) {
            const { optionId, qty } = orderOptions[j];
            const itemOption = availableOptions.find((opt) => opt.optionId === optionId);
            if (itemOption) {
                const {
                    name, salePrice, maxQuantity, posId,
                } = itemOption;
                const optionGroup = optionGroups.find(
                    (group) => group.options.map((opt) => opt.optionId).includes(optionId),
                )!;
                const newOption: OrderOption = {
                    shopOptionId: optionId,
                    optionName: name,
                    optionPrice: salePrice,
                    quantity: qty,
                    totalAmount: salePrice * qty,
                    maxQuantity,
                    posId,
                    ...toReward(itemOption.rewardPolicy),
                };
                options.push({
                    optionGroupId: optionGroup.optionGroupId,
                    maxSelection: optionGroup.maxSelection,
                    minSelection: optionGroup.minSelection,
                    options: [newOption],
                });
            }
        }
        return options;
    };

    const generateOrderLines = async () => {
        const orderLines: OrderLine[] = [];
        for (let i = 0; i < orderItems.length; i += 1) {
            const { itemId, qty, orderOptions } = orderItems[i];
            const item = await ItemApi.getItem(itemId);
            const {
                name, salePrice, imageUrls,
            } = item;
            if (item.isSoldOut) {
                throw new Error(t('toast.soldOut'));
            }
            const defaultOptions = generateOptions(orderOptions, item, 'DEFAULT');
            const personalOptions = generateOptions(orderOptions, item, 'PERSONAL');
            const options = [...defaultOptions, ...personalOptions]
                .map((optionGroup) => optionGroup.options)
                .flat();

            const optionAmount = options
                .map((option) => option.totalAmount)
                .reduce((prev, curr) => prev + curr, 0);
            const totalAmount = (salePrice + optionAmount) * qty;
            const orderItem: OrderLine = {
                shopItemId: itemId,
                itemName: name,
                itemPrice: salePrice,
                status: CartStatus.ON_SALE,
                quantity: qty,
                defaultOptions,
                personalOptions,
                itemImageUrl: imageUrls?.[0],
                thumbnailImage: imageUrls?.[0],
                totalAmount,
                couponUsable: item.couponUsable,
                posId: item.posId,
                ...toReward(item.rewardPolicy),
            };
            orderLines.push(orderItem);
        }
        return orderLines;
    };

    const generateRequest = async () => {
        const orderLines = await generateOrderLines();
        const totalAmount = orderLines
            .map((orderLine) => orderLine.totalAmount)
            .reduce((prev, curr) => prev + curr, 0);

        const options = orderItems.map((order) => order.orderOptions)
            .flat();
        const parsed = orderLines.map(
            (line) => [...line.personalOptions, ...line.defaultOptions].flat()
                .map((group) => group.options).flat(),
        ).flat();
        if (options.length > parsed.length) {
            toast.info(t('toast.excludingUnavailableOptions'));
        }

        const request: CreateOrderRequest = {
            projectId,
            shopId,
            shopName,
            orderPath: 'DIRECT',
            orderType,
            orderLines,
            totalAmount,
            totalDiscount: 0,
            paymentAmount: totalAmount,
            paymentMethod: 'CREDIT_CARD',
            returnUrl: '',
            totalCouponDiscount: 0,
            ...defaultOrderFields,
            totalPointDiscount: 0,
            discounts: [],
        };
        return orderWithTotalAmount(request);
    };

    const { push } = useFlow();
    const toast = useToast();

    const shop = useShop(shopId);
    const isTestUser = useTestUser();
    const { data: testShopData } = useQuery(projectQueries.testShops());
    const reOrderEnabled = useMemo(() => {
        if (testShopData?.shopIds.includes(shopId) && !isTestUser) {
            return false;
        }
        return shop?.orderStatus === 'OPEN'
        && shop?.shopStatus === 'REGISTERED';
    }, [isTestUser, testShopData, shop, shopId]);

    const reOrder = async () => {
        try {
            if (!orderAble?.canOrder) {
                push('ItemSoldOutModal', { shopId });
                return;
            }
            const request = await generateRequest();
            dispatch(orderActions.copy(request));
            push('OrderActivity', { });
        } catch (e: unknown) {
            console.error(e);
            // eslint-disable-next-line no-unsafe-optional-chaining
            if (e instanceof AxiosError && 'message' in e.response?.data) {
                toast.error(e.response?.data.message as string);
            } else if (e instanceof Error) {
                toast.error(e.message);
            } else {
                toast.error(t('toast.unknownError'));
            }
        }
    };

    return {
        reOrder, reOrderEnabled,
    };
}

export default useReOrder;
